import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { SLBPosition } from '@/types';

interface RefreshState {
  isRefreshing: boolean;
  refreshProgress: number;
  refreshIntervalId: NodeJS.Timeout | null;
  refreshStartTime: number | null;
}

interface InventoryDataStore {
  filteredInventoryData: SLBPosition[];
  currentInventoryStateName: string;
  inventoryDataLoading: boolean;
  inventoryDataError: boolean;
  activeInventoryTab: 'inventory' | 'availability' | 'counterparty-reports';
  searchQuery: string;
  inventorySorting: { id: string; desc: boolean }[];
  availabilitySorting: { id: string; desc: boolean } | null;
  refreshState: RefreshState;
  isForceCollecting: boolean;
  isForceCollectSuccess: boolean;
  forceCollectionTaskId: string | null;
  setFilteredInventoryData: (data: SLBPosition[]) => void;
  setCurrentInventoryStateName: (name: string) => void;
  setInventoryDataLoading: (loading: boolean) => void;
  setInventoryDataError: (error: boolean) => void;
  setActiveInventoryTab: (tab: 'inventory' | 'availability' | 'counterparty-reports') => void;
  setSearchQuery: (query: string) => void;
  setInventorySorting: (sorting: { id: string; desc: boolean }[]) => void;
  setAvailabilitySorting: (sorting: { id: string; desc: boolean } | null) => void;
  setRefreshProgress: (progress: number) => void;
  setIsRefreshing: (isRefreshing: boolean) => void;
  setRefreshIntervalId: (id: NodeJS.Timeout | null) => void;
  startRefresh: () => void;
  setRefreshStartTime: (time: number | null) => void;
  clearRefreshState: () => void;
  setIsForceCollecting: (isForceCollecting: boolean) => void;
  setIsForceCollectSuccess: (isSuccess: boolean) => void;
  setForceCollectionTaskId: (taskId: string | null) => void;
  resetForceCollectState: () => void;
}

export const useInventoryDataStore = create<InventoryDataStore>()(
  devtools(
    persist(
      (set) => ({
        filteredInventoryData: [],
        currentInventoryStateName: '',
        inventoryDataLoading: false,
        inventoryDataError: false,
        activeInventoryTab: 'inventory',
        searchQuery: '',
        inventorySorting: [],
        availabilitySorting: { id: 'benchmark_fee', desc: true },
        refreshState: {
          isRefreshing: false,
          refreshProgress: 0,
          refreshIntervalId: null,
          refreshStartTime: null,
        },

        setFilteredInventoryData: (data) => set({ filteredInventoryData: data }),

        setCurrentInventoryStateName: (name) => set({ currentInventoryStateName: name }),

        setInventoryDataLoading: (loading) => set({ inventoryDataLoading: loading }),

        setInventoryDataError: (error) => set({ inventoryDataError: error }),

        setActiveInventoryTab: (tab) => set({ activeInventoryTab: tab }),

        setSearchQuery: (query) => set({ searchQuery: query }),

        setInventorySorting: (sorting) => set({ inventorySorting: sorting }),

        setAvailabilitySorting: (sorting) => set({ availabilitySorting: sorting }),

      setRefreshProgress: (progress) => set((state) => ({
        refreshState: { ...state.refreshState, refreshProgress: progress }
      })),

      setIsRefreshing: (isRefreshing) => set((state) => ({
        refreshState: { ...state.refreshState, isRefreshing }
      })),

      setRefreshIntervalId: (id) => set((state) => ({
        refreshState: { ...state.refreshState, refreshIntervalId: id }
      })),

      startRefresh: () => set((state) => ({
        refreshState: {
          ...state.refreshState,
          isRefreshing: true,
          refreshProgress: 0,
          refreshStartTime: Date.now(),
        }
      })),

      setRefreshStartTime: (time) => set((state) => ({
        refreshState: { ...state.refreshState, refreshStartTime: time }
      })),

      clearRefreshState: () => set({
        refreshState: {
          isRefreshing: false,
          refreshProgress: 0,
          refreshIntervalId: null,
          refreshStartTime: null,
        }
      }),
      isForceCollecting: false,
      isForceCollectSuccess: false,
      forceCollectionTaskId: null,
      setIsForceCollecting: (isForceCollecting) => set({ isForceCollecting }),
      setIsForceCollectSuccess: (isSuccess) => set({ isForceCollectSuccess: isSuccess }),
      setForceCollectionTaskId: (taskId) => set({ forceCollectionTaskId: taskId }),
      resetForceCollectState: () => set({ isForceCollecting: false, isForceCollectSuccess: false, forceCollectionTaskId: null }),
      }),
      {
        name: 'inventory-data-store',
        // v0 → v1: inventorySorting changed from `{id;desc} | null` (LR-284
        // single-sort) to `{id;desc}[]` (LR-297 multi-sort). Devs who used
        // the LR-284 branch pre-merge may have the old shape in localStorage;
        // without this migrate, default zustand shallow-merge would hydrate
        // that shape into the store and InventoryTabPanel's `sorting.find`
        // would throw on first render.
        version: 1,
        migrate: (persistedState: unknown, version: number) => {
          const s = persistedState as Record<string, unknown> | null;
          if (s && version < 1) {
            const prev = s.inventorySorting;
            if (prev == null) {
              s.inventorySorting = [];
            } else if (!Array.isArray(prev) && typeof prev === 'object') {
              s.inventorySorting = [prev];
            }
          }
          return s;
        },
        partialize: (state) => ({
          activeInventoryTab: state.activeInventoryTab,
          searchQuery: state.searchQuery,
          availabilitySorting: state.availabilitySorting,
          // Captured by inventory-adapter (saved views). Must persist so
          // an F5 with the same active view doesn't surface ghost dirty —
          // see the INVARIANT comment in inventory-adapter.ts:capture.
          inventorySorting: state.inventorySorting,
        }),
      }
    ),
    {
      name: 'inventory-data-store', enabled: import.meta.env.DEV,
    }
  )
);

